import { useState, useEffect } from "react";

// ─── Dark Mode Sync ───────────────────────────────────────────────────────────

function getHeaderDarkMode() {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem("trw-dark-mode");
  if (stored === "1") return true;
  if (stored === "0") return false;
  return (
    document.documentElement.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("dark")
  );
}

function useSyncedDarkMode() {
  const [isDark, setIsDark] = useState(getHeaderDarkMode);

  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(getHeaderDarkMode()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class", "style"] });

    const onStorage = (e) => {
      if (e.key === "trw-dark-mode") setIsDark(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return isDark;
}

// ─── Rich text renderer ───────────────────────────────────────────────────────
// Parses **bold** markers in plain strings — no JSX at module level (OXC-safe)

function Rich({ children }) {
  if (typeof children !== "string") return children;
  const parts = children.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </>
  );
}

// ─── Content Data (plain JS strings only — OXC-safe) ─────────────────────────

const introLines = [
  "Welcome to **Travel.RethinkWays.com** (\"we\", \"our\", or \"us\"). By accessing or using this website www.travel.rethinkways.com, you agree to comply with and be bound by these Terms & Conditions. Please read them carefully before using our Site.",
  "**Travel.Rethinkways.com** is powered by and legally operated by **GD Brand Creative**, which manages the platform, content, and associated services.",
];

const sections = [
  {
    title: "Eligibility",
    paragraphs: [
      "You must be at least **18 years old** and legally capable of entering into binding agreements under Indian law to use this website. By accessing or using the website, you represent that you meet these requirements.",
    ],
  },
  {
    title: "Registration & Subscriptions",
    paragraphs: [
      "If you subscribe to our newsletter or updates, you agree to provide accurate and current information. You are responsible for ensuring that your email address remains active and accessible to receive our communications.",
      "Some of our blog posts may include **affiliate links or sponsored recommendations.** If you click or book through these links, we may earn a commission, at no extra cost to you. These links may direct you to third-party websites that we do not control.",
      "In some cases, we may connect you with travel agencies, hotels, or tour operators. By sharing your details, you consent to being contacted by these third parties. We are not responsible for their services, offers, or conduct.",
    ],
  },
  {
    title: "Content Usage",
    paragraphs: [
      "All content on **Travel.RethinkWays.com**, including articles, graphics, branding, and curated travel resources, is protected by applicable intellectual property laws. You may not reproduce, republish, or distribute any material without prior written permission.",
    ],
  },
  {
    title: "Limitation of Liability",
    paragraphs: [
      "To the fullest extent permitted by law, **Travel.RethinkWays.com (GD Brand Creative)**, its team, and affiliates shall not be liable for any **direct, indirect, incidental, consequential, or special damages** arising from your use of the Website or reliance on its content.",
      "This includes, but is not limited to, loss of data, financial loss, cancellations, or dissatisfaction with third-party services.",
    ],
  },
  {
    title: "Indemnification",
    paragraphs: [
      "You agree to indemnify and hold harmless **Travel.RethinkWays.com (GD Brand Creative)**, its team and partners from any claims, damages, liabilities, or expenses arising out of your use of the Site, your User Content, or your violation of these Terms.",
    ],
  },
  {
    title: "Privacy & Data Protection",
    paragraphs: [
      "Your use of this Site is also governed by our **Privacy Policy**, which explains how we collect, use, and protect your personal information. By using our Site, you agree to the terms of our Privacy Policy.",
    ],
  },
  {
    title: "Communication Consent",
    paragraphs: [
      "By providing your contact details, you consent to receive communications from us regarding updates, promotions, or travel-related offers. You may opt out of such communications at any time.",
    ],
  },
  {
    title: "Modification & Termination",
    paragraphs: ["We reserve the right to"],
    bullets: [
      "Modify or discontinue any part of the Website",
      "Update these Terms at any time",
      "Suspend or terminate access without prior notice",
    ],
    extraParagraphs: [
      "We may update these Terms & Conditions from time to time. Changes will be posted on this page with the revised effective date. Continued use of our Site means you accept the updated Terms.",
    ],
  },
  {
    title: "Governing Law & Jurisdiction",
    paragraphs: [
      "These Terms shall be governed by and construed under the laws of **India** and any disputes will be subject to the exclusive jurisdiction of the courts in **Chennai, Tamil Nadu.**",
    ],
  },
  {
    title: "Contact Us",
    paragraphs: ["If you have questions, feedback, or complaints, contact us"],
    contactLines: [
      { label: "Legal Entity", value: "GD Brand Creative" },
      { label: "Brand Name", value: "Travel Rethink Ways" },
      { label: "Email", value: "travel@rethinkways.com", href: "mailto:travel@rethinkways.com" },
    ],
  },
];

// ─── Style helpers ────────────────────────────────────────────────────────────

const textPrimary = (dark) => ({ color: dark ? "#ffffff" : "rgb(13, 19, 23)" });
const textMuted   = (dark) => ({ color: dark ? "rgba(255,255,255,0.82)" : "rgba(13,19,23,0.76)" });

// ─── Component ────────────────────────────────────────────────────────────────

export default function TermsAndConditions() {
  const isDark = useSyncedDarkMode();
  const container = { width: "100%", maxWidth: 1500, padding: "0 24px", margin: "0 auto" };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isDark ? "#000000" : "#ffffff",
        color: isDark ? "#ffffff" : "rgb(13, 19, 23)",
        fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
        WebkitFontSmoothing: "antialiased",
        textRendering: "optimizeLegibility",
        transition: "background 200ms, color 200ms",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Yeseva+One&display=swap');
        .trw-tc-h1 {
          font-family: 'Yeseva One', Georgia, 'Times New Roman', serif !important;
          font-weight: 400 !important;
        }
      `}</style>

      <main style={{ padding: "40px 0 0px" }}>

        {/* ── Hero ── */}
        <section style={container}>

          {/* Title */}
          <h1
            className="trw-tc-h1"
            style={{
              margin: 0,
              fontFamily: "'Yeseva One', Georgia, 'Times New Roman', serif",
              fontWeight: 400,
              fontSize: "41px",
              lineHeight: "51px",
              letterSpacing: 0,
              ...textPrimary(isDark),
            }}
          >
            Terms and Conditions
          </h1>

          {/* Effective Date */}
          <p
            style={{
              margin: "22px 0 0",
              fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "29px",
              ...textPrimary(isDark),
            }}
          >
            <strong style={{ fontWeight: 600 }}>Effective Date:</strong> 01.10.2025
          </p>

          {/* Intro paragraphs */}
          <div style={{ marginTop: "20px", display: "grid", gap: "22px", marginBottom: "36px" }}>
            {introLines.map((line, i) => (
              <p
                key={i}
                style={{
                  margin: 0,
                  fontWeight: 400,
                  fontSize: "17px",
                  lineHeight: "30px",
                  ...textMuted(isDark),
                }}
              >
                <Rich>{line}</Rich>
              </p>
            ))}
          </div>
        </section>

        {/* ── Article Sections ── */}
        <article style={container}>
          {sections.map((section, idx) => (
            <section
              key={idx}
              style={{
                borderTop: 0,
                marginTop: idx === 0 ? 0 : "36px",
              }}
            >
              {/* Section heading */}
              {section.title && (
                <h2
                  style={{
                    margin: "0 0 12px",
                    fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
                    fontWeight: 600,
                    fontSize: "28px",
                    lineHeight: "39px",
                    ...textPrimary(isDark),
                  }}
                >
                  {section.title}
                </h2>
              )}

              {/* Paragraphs */}
              {section.paragraphs && section.paragraphs.map((para, i) => (
                <p
                  key={i}
                  style={{ margin: 0, fontSize: "17px", lineHeight: "30px", ...textMuted(isDark) }}
                >
                  <Rich>{para}</Rich>
                </p>
              ))}

              {/* Bullet list */}
              {section.bullets && section.bullets.length > 0 && (
                <ul style={{ margin: "8px 0 0 38px", padding: 0, listStyle: "disc" }}>
                  {section.bullets.map((bullet, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: "17px",
                        lineHeight: "30px",
                        marginTop: i > 0 ? "2px" : 0,
                        ...textMuted(isDark),
                      }}
                    >
                      <Rich>{bullet}</Rich>
                    </li>
                  ))}
                </ul>
              )}

              {/* Extra paragraphs */}
              {section.extraParagraphs && section.extraParagraphs.map((para, i) => (
                <p
                  key={i}
                  style={{
                    margin: "10px 0 0",
                    fontSize: "17px",
                    lineHeight: "30px",
                    ...textMuted(isDark),
                  }}
                >
                  <Rich>{para}</Rich>
                </p>
              ))}

              {/* Contact lines */}
              {section.contactLines && section.contactLines.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  {section.contactLines.map((line, i) => (
                    <p
                      key={i}
                      style={{ margin: 0, fontSize: "17px", lineHeight: "30px", ...textMuted(isDark) }}
                    >
                      <strong style={{ fontWeight: 600, ...textPrimary(isDark) }}>
                        {line.label}:
                      </strong>{" "}
                      {line.href ? (
                        <a
                          href={line.href}
                          style={{
                            textDecoration: "underline",
                            textUnderlineOffset: "3px",
                            ...textPrimary(isDark),
                          }}
                        >
                          {line.value}
                        </a>
                      ) : (
                        line.value
                      )}
                    </p>
                  ))}
                </div>
              )}
            </section>
          ))}
        </article>
      </main>
    </div>
  );
}
